<template>
  <div v-loading="loading" class="aliyun-video-player-wrapper">
    <div :id="playerContainerId" class="aliyun-video-player-container"></div>
  </div>
</template>

<script>
import Aliplayer from "aliyun-aliplayer";
import { getStsCredentials } from "@/service/alibabaCloud.service";

export default {
  name: "aliyunVideoPlayer",
  props: {
    /** 视频信息：{ VideoId, RegionId, HasHlsAliyunVideo } */
    videoInfo: {
      type: Object,
      default: null,
    },
    /** 播放器高度 */
    height: {
      type: String,
      default: "500px",
    },
    /** 播放器宽度 */
    width: {
      type: String,
      default: "100%",
    },
    /** 是否隐藏播放器错误信息 */
    hideErrorMessage: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      playerInstance: null,
      loading: false,
      canPlayVideo: false
    };
  },
  computed: {
    playerContainerId() {
      return `aliyunVideoPlayer_${this._uid}`;
    },
  },
  watch: {
    videoInfo: {
      handler(val) {
        if (val && val.VideoId) {
          this.$nextTick(() => this.createPlayer());
        } else {
          this.clearPlayer();
        }
      },
      immediate: false,
    },
  },
  mounted() {
    if (this.videoInfo && this.videoInfo.VideoId) {
      this.$nextTick(() => this.createPlayer());
    }
  },
  beforeDestroy() {
    this.clearPlayer();
  },
  methods: {
    getMainDomain(host) {
      if (
        host === "localhost" ||
        /^(\d{1,3}\.){3}\d{1,3}$/.test(host) ||
        host.includes(":")
      ) {
        return host;
      }
      const parts = host.split(".");
      if (parts.length > 2) {
        return parts.slice(-2).join(".");
      }
      return host;
    },
    async getStsCredentialsWithRetry(maxRetries = 3) {
      let retryCount = 0;
      while (retryCount < maxRetries) {
        try {
          const result = await getStsCredentials(this.videoInfo.RegionId, this.videoInfo.VideoId);
          if (result && result.success) return result;
          retryCount++;
          if (retryCount < maxRetries) {
            await new Promise((r) =>
              setTimeout(r, 2000 * retryCount)
            );
          }
        } catch (error) {
          retryCount++;
          if (retryCount >= maxRetries) throw error;
          await new Promise((r) =>
            setTimeout(r, 2000 * retryCount)
          );
        }
      }
      throw new Error(`获取STS凭证失败，已重试${maxRetries}次`);
    },
    checkStsCredentialExpiry(credentials) {
      if (!credentials.Expiration) return;
      const expirationTime = new Date(credentials.Expiration);
      const currentTime = new Date();
      const minutesLeft = Math.floor(
        (expirationTime.getTime() - currentTime.getTime()) / (1000 * 60)
      );
      if (minutesLeft < 5) {
        this.$message.warning(
          `视频播放权限将在${minutesLeft}分钟后过期，请及时保存进度`
        );
      }
    },
    async createPlayer() {
      this.clearPlayer();
      if (!this.videoInfo || !this.videoInfo.VideoId) return;

      this.loading = true;
      try {
        const result = await this.getStsCredentialsWithRetry();
        if (!result || !result.success) {
          this.$message.error("获取视频播放权限失败，请稍后重试");
          this.loading = false;
          return;
        }

        const credentials = result.data;
        if (
          !credentials.AccessKeyId ||
          !credentials.AccessKeySecret ||
          !credentials.SecurityToken
        ) {
          this.$message.error("STS凭证不完整，无法播放DRM加密视频");
          this.loading = false;
          return;
        }

        this.checkStsCredentialExpiry(credentials);
        const hostname = this.getMainDomain(window.location.hostname);

        this.$nextTick(() => {
          const container = document.getElementById(this.playerContainerId);
          if (!container) {
            this.loading = false;
            return;
          }
          try {
            let useDrm = true;
            let authTimeout = 3600;
            if (!!this.videoInfo && !!this.videoInfo.Duration && this.videoInfo.Duration > authTimeout){
              authTimeout = parseInt(this.videoInfo.Duration);
            }
            
            const drmSupport = this.checkBrowserDRMSupport();
            const config = {
              id: this.playerContainerId,
              width: this.width,
              height: this.height,
              autoplay: false,
              authTimeout: authTimeout,
              vid: this.videoInfo.VideoId,
              accessKeyId: credentials.AccessKeyId,
              accessKeySecret: credentials.AccessKeySecret,
              securityToken: credentials.SecurityToken,
              region: credentials.RegionId,
              mediaType: "video",
              license: {
                domain: hostname,
                key: credentials.LicenseKey,
              },
              useH5Prism: true,
              useFlashPrism: false,
              loading: true,
              preload: true,
              keyShortCuts: true
            };

            if (useDrm){
              let format = 'm3u8';
              //if (!!credentials.DrmFormat){
              //  format = credentials.DrmFormat;
              //}
              config.format = format;//'m3u8';// 明确指定HLS格式
              config.isDrm = true;
              config.enableDRM = true;
              config.drmSessionTimeout = 20000;
            } 
            if (useDrm && drmSupport.supportsFairPlay){
              config.certId = credentials.CertId;
            }
            this.playerInstance = new Aliplayer(config, (player) => {
              this.$emit("ready", player);
            });

            if (this.playerInstance) {
              this.playerInstance.on('ready', this.onPlayerReady);    
              this.playerInstance.on("error", this.onPlayerError);
              this.playerInstance.on("canplay", this.onPlayerCanplay);
              this.playerInstance.on("play", this.onPlayerPlay);
              this.playerInstance.on("pause", this.onPlayerPause);
            }
          } catch (e) {
            this.loading = false;
            console.error("初始化阿里云播放器失败", e);
            this.$message.error("播放器初始化失败: " + (e.message || e));
          }
        });
      } catch (error) {
        this.loading = false;
        console.error("获取STS凭证时发生错误:", error);
        this.$message.error(
          "获取视频播放权限时发生错误: " + (error.message || error)
        );
      }
    },
    clearPlayer() {
      if (this.playerInstance) {
        this.playerInstance.off('ready', this.onPlayerReady);    
        this.playerInstance.off("error", this.onPlayerError);
        this.playerInstance.off("canplay", this.onPlayerCanplay);
        this.playerInstance.off("play", this.onPlayerPlay);
        this.playerInstance.off("pause", this.onPlayerPause);
        this.playerInstance.dispose();
        this.playerInstance = null;
      }
      this.loading = false;
    },
    onPlayerReady(){
      setTimeout(()=>{
        this.loading = false;
      }, 8000);
      //console.log('播放器准备就绪');
    },
    onPlayerError(error) {
      console.error("播放器错误:", error);
      if (this.hideErrorMessage) {
        const el = document.querySelector(".prism-ErrorMessage");
        if (el) el.style.display = "none";
      }
      this.$emit("error", error);
    },
    onPlayerCanplay() {
      this.canPlayVideo = true;
      this.loading = false;
      this.$emit("canplay");
    },
    onPlayerPlay(e) {
      setTimeout(() => {
        const t = this.playerInstance.getCurrentTime();
        if (!this.canPlayVideo && t < 5){
          this.playerInstance.seek(5);
        }
        this.$emit("play", e);
      }, 100);
      
    },
    onPlayerPause(e) {
      this.$emit("pause", e);
    },
    // 检查浏览器DRM支持情况
    checkBrowserDRMSupport() {
      const userAgent = navigator.userAgent.toLowerCase();
      const isChrome = userAgent.includes('chrome');
      const isFirefox = userAgent.includes('firefox');
      const isSafari = userAgent.includes('safari') && !userAgent.includes('chrome');
      const isEdge = userAgent.includes('edge');
      
      console.log('浏览器信息:', {
        userAgent: navigator.userAgent,
        isChrome,
        isFirefox,
        isSafari,
        isEdge
      });
      
      // 检查Widevine支持（Chrome, Firefox, Edge）
      if (isChrome || isFirefox || isEdge) {
        if (navigator.requestMediaKeySystemAccess) {
          navigator.requestMediaKeySystemAccess('com.widevine.alpha', [{
            initDataTypes: ['cenc'],
            audioCapabilities: [{
              contentType: 'audio/mp4;codecs="mp4a.40.2"',
              robustness: 'SW_SECURE_CRYPTO'
            }],
            videoCapabilities: [{
              contentType: 'video/mp4;codecs="avc1.42E01E"',
              robustness: 'SW_SECURE_CRYPTO'
            }]
          }]).then(() => {
            console.log('浏览器支持Widevine DRM');
          }).catch((error) => {
            console.warn('浏览器不支持Widevine DRM:', error);
          });
        }
      }
      
      // 检查FairPlay支持（Safari）
      if (isSafari) {
        console.log('Safari浏览器，支持FairPlay DRM');
      }
      
      return {
        supportsWidevine: isChrome || isFirefox || isEdge,
        supportsFairPlay: isSafari
      };
    }
  },
};
</script>

<style lang="scss" scoped>
.aliyun-video-player-wrapper {
  min-height: 200px;
}
.aliyun-video-player-container {
  width: 100%;
}
</style>

<style lang="scss">
@import "aliyun-aliplayer/build/skins/default/aliplayer-min.css";
</style>



// WEBPACK FOOTER //
// src/components/aliyunVod/aliyunVideoPlayer.vue